/**
 * ч.1033: позначки рядків у «Розрахунках за документами» — підсумок
 * залишку боргу позначених документів над таблицею (запит власника).
 *
 * Чисті функції окремо від сторінки — щоб їх можна було перевірити
 * тестом без ag-Grid.
 */
import type { SettlementByDocumentRow } from '@/api/documents';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';

/** Ключ рядка для вибору. Звіт групує рухи по ЧОТИРЬОХ полях (тип
 *  документа, документ, контрагент, договір) — унікальна лише вся
 *  четвірка. Ключ лише по документу склеїв би різні рядки: ag-Grid тоді
 *  плутає позначки, а сума рахує не те. */
export function settlementRowKey(r: Pick<SettlementByDocumentRow,
  'settlement_doc_type' | 'settlement_doc_id' | 'contractor_id' | 'contract_id'>): string {
  return `${r.settlement_doc_type}|${r.settlement_doc_id}|${r.contractor_id}|${r.contract_id}`;
}

/** Сума залишку боргу позначених рядків. У регістрі 4 знаки після коми,
 *  тому рахуємо в цілих десятитисячних: додавання дробів з плаваючою
 *  комою дає «хвости» (0,1 + 0,2 ≠ 0,3), а тут гроші. */
export function sumOpenAmount(rows: Pick<SettlementByDocumentRow, 'open_amount'>[]): number {
  const units = rows.reduce((s, r) => s + Math.round(Number(r.open_amount) * 10000), 0);
  return units / 10000;
}

/** ч.1039: підпис документа-джерела боргу. Реєстр тепер відкриває борг
 *  БУДЬ-ЯКИМ документом з проводкою на рахунок розрахунків (не лише
 *  накладною): довідка, коригування, залишки, акт, повернення, ВМД… —
 *  підпис в однині, як у комірці «Документ»; невідомий тип → назва списку
 *  з DOC_TABLE_NAMES_UK, зовсім невідомий → сирий код без «doc.». */
const DOC_LABELS: Record<string, string> = {
  'doc.goods_sales': 'Реалізація',
  'doc.goods_receipts': 'Надходження',
  'doc.service_acts': 'Акт послуг',
  'doc.manual_operations': 'Довідка',
  'doc.debt_adjustments': 'Коригування боргу',
  'doc.opening_balances': 'Вступні залишки',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.additional_expenses': 'Додаткові витрати',
  'doc.bank_receipts': 'Надходження на рахунок',
  'doc.bank_payments': 'Платіж з рахунку',
  'doc.cash_receipts': 'Прибутковий ордер',
  'doc.cash_payments': 'Видатковий ордер',
  'doc.customs_declarations': 'ВМД',
  'doc.fx_revaluations': 'Переоцінка валюти',
  'doc.processing_services_sales': 'Послуги переробки',
  'doc.production_service_acts': 'Акт виробничих послуг',
  'doc.commission_sale': 'Продаж комісіонером',
  'doc.commission_settlement_report': 'Звіт комісіонера',
  'doc.advance_reports': 'Авансовий звіт',
};

export function settlementDocLabel(type: string): string {
  return DOC_LABELS[type] ?? DOC_TABLE_NAMES_UK[type] ?? type.replace(/^doc\./, '');
}

// ч.1184: слово «документ» у числі — спільне зі списками банку.
export { docsWord } from '@/shared/utils/selectionSum';
